import styled from 'styled-components'
import { ContainerProps } from './Container.types'
import Box from '../Box/Box'

/**
 * Max widths aligned with theme breakpoints:
 * - xs: 444px (compact mobile)
 * - sm: 640px (theme.breakpoints.mobile)
 * - md: 1024px (theme.breakpoints.tablet)
 * - lg: 1280px (theme.breakpoints.desktop)
 * - xl: 1536px (large desktop)
 */
const MAX_WIDTHS = {
  xs: '444px',
  sm: '640px',
  md: '1024px',
  lg: '1280px',
  xl: '1536px',
}

const getMaxWidth = (
  maxWidth: ContainerProps['maxWidth']
): string | undefined => {
  if (maxWidth === false) return undefined
  if (typeof maxWidth === 'string' && maxWidth in MAX_WIDTHS) {
    return MAX_WIDTHS[maxWidth as keyof typeof MAX_WIDTHS]
  }
  if (typeof maxWidth === 'number') return `${maxWidth}px`
  if (typeof maxWidth === 'string') return maxWidth
  return MAX_WIDTHS.lg
}

export const StyledContainer = styled(Box)<{
  $maxWidth?: ContainerProps['maxWidth']
  $fixed?: boolean
  $disableGutters?: boolean
}>`
  width: 100%;
  margin-left: auto;
  margin-right: auto;

  ${({ $maxWidth = 'lg' }) => {
    const width = getMaxWidth($maxWidth)
    return width && `max-width: ${width};`
  }}

  /* Mobile-first padding: base mobile, then larger screens */
  ${({ $disableGutters, theme }) =>
    !$disableGutters &&
    `
    padding-left: ${theme.spacing.medium};
    padding-right: ${theme.spacing.medium};

    @media (min-width: ${theme.breakpoints.mobile}) {
      padding-left: ${theme.spacing.large};
      padding-right: ${theme.spacing.large};
    }

    @media (min-width: ${theme.breakpoints.tablet}) {
      padding-left: ${theme.spacing.xlarge};
      padding-right: ${theme.spacing.xlarge};
    }
  `}

  /* Fixed width container with responsive max-widths */
  ${({ $fixed, theme }) =>
    $fixed &&
    `
    @media (min-width: 0px) {
      max-width: ${MAX_WIDTHS.xs};
    }
    @media (min-width: ${theme.breakpoints.mobile}) {
      max-width: ${MAX_WIDTHS.sm};
    }
    @media (min-width: ${theme.breakpoints.tablet}) {
      max-width: ${MAX_WIDTHS.md};
    }
    @media (min-width: ${theme.breakpoints.desktop}) {
      max-width: ${MAX_WIDTHS.lg};
    }
    @media (min-width: 1536px) {
      max-width: ${MAX_WIDTHS.xl};
    }
  `}
`
